import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Calendar } from "lucide-react";
import Placeholder from "@/components/Placeholder";
import { Reveal, StaggerGroup, StaggerItem, FloatingBlob, Magnetic } from "@/components/Motion";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page" className="relative overflow-hidden min-h-screen">
      <SEO title="Medical Blog" description="Read the latest insights and articles on women's health." />
      <FloatingBlob color="aurora-warm" size={500} top="-10%" right="-5%" delay={0} />

      <section className="py-16 lg:py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="overline mb-4">Insights</div>
            <h1 className="font-heading text-5xl md:text-6xl text-[var(--color-text)] leading-[1.05] max-w-4xl">
              The Journal — <em className="text-[var(--color-coral)] not-italic">notes from the practice.</em>
            </h1>
            <p className="mt-6 text-lg text-[var(--color-text-muted)] max-w-2xl leading-relaxed">
              Trusted, jargon-free guidance on pregnancy, fertility, hormonal health, and women&apos;s wellness — written for the patients who ask the most thoughtful questions.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 relative">
        <FloatingBlob color="aurora-purple" size={400} bottom="10%" left="-10%" delay={2} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="glass-card rounded-2xl p-6 border border-[var(--color-border)] animate-pulse h-80" />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <Reveal>
              <div className="text-center py-20 glass-card rounded-3xl border border-[var(--color-border)]" data-testid="blog-empty-state">
                <div className="w-16 h-16 rounded-full bg-[var(--color-coral-light)] flex items-center justify-center mx-auto mb-6 border border-[var(--color-coral)]/30 shadow-[0_0_15px_var(--color-coral-glow)]">
                  <BookOpen className="w-7 h-7 text-[var(--color-coral)]" strokeWidth={1.5} />
                </div>
                <h2 className="font-heading text-3xl text-white">Insights coming soon.</h2>
                <p className="mt-3 text-white/80 max-w-md mx-auto">
                  The journal is being curated. The first articles — on pregnancy, fertility and hormonal health — will be published shortly.
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-4">
                  <Magnetic><Link to="/appointment" className="btn-primary text-sm">Book Consultation <ArrowRight className="w-4 h-4" /></Link></Magnetic>
                  <Magnetic><Link to="/contact" className="btn-outline text-sm">Have a question?</Link></Magnetic>
                </div>
              </div>
            </Reveal>
          ) : (
            <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" stagger={0.1}>
              {posts.map((p) => (
                <StaggerItem key={p.id}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-post-${p.slug}`}
                    className="group glass-card block rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-coral)] transition-all overflow-hidden h-full"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      {p.cover_image ? (
                        <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <Placeholder variant="wide" label={p.category || "Article"} className="group-hover:scale-105 transition-transform duration-500" />
                      )}
                    </div>
                    <div className="p-6">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--color-coral)] font-semibold">{p.category || "Insight"}</div>
                      <h3 className="mt-2 font-heading text-2xl text-white leading-tight group-hover:text-[var(--color-coral)] transition-colors">{p.title}</h3>
                      {p.excerpt && <p className="mt-3 text-sm text-white/80 leading-relaxed line-clamp-3">{p.excerpt}</p>}
                      <div className="mt-5 flex items-center gap-2 text-xs text-white/60">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(p.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })}
                      </div>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerGroup>
          )}
        </div>
      </section>
    </div>
  );
}
